import { useMemo, useState } from "react";
import {
  arquivarPendencia,
  dataHora,
  desarquivarPendencia,
  numero,
  reprocessarPendencia,
  useTriagem,
  usePainel,
  type LinhaTriagem,
} from "../lib/admin";
import { Chip, Th, Vazio } from "./painel";
import { IconeArquivar, IconeDevolver, IconeDivisa, IconeDossie, IconeReprocessar } from "./icones";

/**
 * Suporte → Triagem: o que a esteira não conseguiu aplicar.
 *
 * ## Por que esta tela existe
 *
 * 17 pendências ficaram QUATORZE DIAS na fila sem ninguém olhar, porque o alerta
 * não tem canal. Victor decidiu em 18/08 que a triagem vive no painel — o que
 * aceita, de olhos abertos, que o aviso é *pull*: só funciona se alguém abrir.
 * Então a tela tem de ser boa o bastante para isso acontecer, e é daí que vem
 * cada decisão de desenho abaixo.
 *
 * ## As três decisões que não são estilo
 *
 * **1. Agrupa por CLASSE de motivo.** Hoje 18 das 27 linhas têm o mesmo erro,
 * variando só o uuid. Lista plana seria rolagem de uuid e ninguém leria nenhuma.
 * Quem normaliza o texto é o banco (`motivo_classe`), não este arquivo.
 *
 * **2. Diz a PROCEDÊNCIA do aluno, ou diz que não sabe.** O payload de
 * cancelamento não carrega e-mail; 18 das 27 pendências não resolvem aluno de
 * lugar nenhum. Campo vazio pareceria defeito da tela, e chutar seria pior:
 * apontar o dossiê errado é pior que não apontar nenhum.
 *
 * **3. O recado descreve a CONSEQUÊNCIA, nunca "salvo".** `replay_evento`
 * devolve `null` tanto para "recusei em silêncio" quanto para "reapliquei e deu
 * certo"; a RPC relê a linha e devolve o que aconteceu. Reprocessar um estorno
 * órfão falha igual em 18 de 18 casos, e a tela precisa dizer isso — senão o
 * operador clica, lê "sucesso", e a linha continua lá.
 *
 * ## Onde o banco manda, e a tela só obedece
 *
 * `pode_reprocessar` vem de `triagem_fila`, com a MESMA regra que a RPC aplica.
 * A tela não recalcula: se as duas discordassem, o botão mentiria. E pendência
 * de `aviso` não tem o botão **e** é recusada no banco — reprocessar um evento
 * já aplicado zera o `processado_em`, que não fica guardado em lugar nenhum.
 */
export default function SuporteTriagem() {
  const [arquivadas, setArquivadas] = useState(false);
  const { estado, recarregar, recarregando } = useTriagem(arquivadas);
  const { estado: estadoPainel } = usePainel();

  const [filtroSeveridade, setFiltroSeveridade] = useState<"todas" | "falha" | "aviso">("todas");
  const [filtroOrigem, setFiltroOrigem] = useState("todas");
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());
  const [selecao, setSelecao] = useState<Set<string>>(new Set());
  const [acao, setAcao] = useState<Acao | null>(null);
  const [recado, setRecado] = useState<Recado | null>(null);

  const linhas = estado.fase === "pronto" ? estado.linhas : [];

  const origens = useMemo(
    () => [...new Set(linhas.map((l) => l.origem))].sort(),
    [linhas],
  );

  const visiveis = useMemo(
    () =>
      linhas.filter(
        (l) =>
          (filtroSeveridade === "todas" || l.severidade === filtroSeveridade) &&
          (filtroOrigem === "todas" || l.origem === filtroOrigem),
      ),
    [linhas, filtroSeveridade, filtroOrigem],
  );

  // Falha antes de aviso: falha é trabalho pendente, aviso é informação sobre uma
  // aplicação que ACONTECEU. Dentro de cada severidade, o grupo maior primeiro.
  const grupos = useMemo(() => {
    const mapa = new Map<string, LinhaTriagem[]>();
    for (const l of visiveis) {
      // A chave inclui a severidade para que a MESMA classe de motivo em falha e
      // em aviso não caia num grupo só. O separador é espaço: `severidade` só vale
      // 'falha' ou 'aviso', nenhuma das duas tem espaço, então não há colisão.
      //
      // Aqui havia um byte NUL literal, e o efeito foi pior que estético: o git
      // classificava o arquivo como BINÁRIO. As 679 linhas desta tela nunca
      // apareceram em diff nenhum, o `git grep` respondia "Binary file matches" e
      // o ripgrep omitia o arquivo em silêncio. Num projeto cujo CLAUDE.md trata
      // os comentários como memória, a memória desta tela ficou fora da busca.
      const chave = `${l.severidade} ${l.motivo_classe}`;
      const atual = mapa.get(chave);
      if (atual) atual.push(l);
      else mapa.set(chave, [l]);
    }
    return [...mapa.entries()]
      .map(([chave, itens]) => ({
        chave,
        severidade: itens[0].severidade,
        classe: itens[0].motivo_classe,
        itens,
      }))
      .sort((a, b) => {
        if (a.severidade !== b.severidade) return a.severidade === "falha" ? -1 : 1;
        return b.itens.length - a.itens.length;
      });
  }, [visiveis]);

  const falhas = linhas.filter((l) => l.severidade === "falha").length;
  const avisos = linhas.filter((l) => l.severidade === "aviso").length;

  function fechar(r: Recado | null) {
    setAcao(null);
    setSelecao(new Set());
    if (r) setRecado(r);
    if (r && r.tom !== "erro") recarregar();
  }

  function alternarGrupo(chave: string) {
    setColapsados((antes) => {
      const novo = new Set(antes);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  }

  function alternarSelecao(chave: string) {
    setSelecao((antes) => {
      const novo = new Set(antes);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  }

  if (estado.fase === "carregando") {
    return <Vazio>Carregando a fila…</Vazio>;
  }
  if (estado.fase === "erro") {
    return (
      <p role="alert" className="rounded-cf bg-erro-fundo px-4 py-3 text-sm text-erro-tinta">
        Não deu para ler a fila da triagem. Tente atualizar; se insistir, é o banco.
      </p>
    );
  }
  if (estado.fase === "negado") {
    return (
      <p className="rounded-cf bg-alerta-fundo px-4 py-3 text-sm leading-relaxed text-alerta-tinta">
        Não deu para confirmar seu acesso à triagem. Ela é do Suporte — se você
        deveria ter, peça a um admin.
      </p>
    );
  }

  const selecionadas = visiveis.filter((l) => selecao.has(chaveDe(l)));

  return (
    <div className="space-y-6">
      {/* ---- cabeçalho: o que está na fila, e desde quando ------------------ */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold text-titulo">
            {arquivadas ? "Pendências arquivadas" : "A fila da triagem"}
          </h2>
          <p className="mt-0.5 text-sm leading-relaxed text-cinza">
            {arquivadas
              ? "Decisões humanas de que a pendência não é aplicável. Nada foi apagado, e a linha volta sozinha se o erro mudar."
              : "O que a esteira recebeu e não conseguiu aplicar. Falha = não aplicado; aviso = aplicado com divergência conhecida."}
          </p>
          <p className="mt-1 text-xs text-cinza">lido às {dataHora(estado.lidoEm)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip tom="erro">{numero(falhas)} falha(s)</Chip>
          <Chip tom="atencao">{numero(avisos)} aviso(s)</Chip>
          <button
            type="button"
            onClick={() => {
              setRecado(null);
              setSelecao(new Set());
              setArquivadas((a) => !a);
            }}
            className="btn-contorno"
          >
            {arquivadas ? "Ver a fila aberta" : "Ver arquivadas"}
          </button>
          <button type="button" onClick={recarregar} disabled={recarregando} className="btn-contorno">
            {recarregando ? "Atualizando…" : "Atualizar"}
          </button>
        </div>
      </div>

      {recado && (
        <p
          role="status"
          className={`rounded-cf px-4 py-3 text-sm leading-relaxed ${
            recado.tom === "erro"
              ? "bg-erro-fundo text-erro-tinta"
              : recado.tom === "atencao"
                ? "bg-alerta-fundo text-alerta-tinta"
                : "bg-aviso-fundo text-aviso-tinta"
          }`}
        >
          {recado.texto}
        </p>
      )}

      {acao && <FormularioDeAcao key={chaveDaAcao(acao)} acao={acao} aoFechar={fechar} />}

      {/* ---- filtros ------------------------------------------------------- */}
      {linhas.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5">
            <span className="text-cinza">Severidade</span>
            <select
              value={filtroSeveridade}
              onChange={(e) => setFiltroSeveridade(e.target.value as "todas" | "falha" | "aviso")}
              className="rounded-cf border border-borda bg-superficie px-2 py-1 text-tinta"
            >
              <option value="todas">todas</option>
              <option value="falha">só falhas</option>
              <option value="aviso">só avisos</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-cinza">Origem</span>
            <select
              value={filtroOrigem}
              onChange={(e) => setFiltroOrigem(e.target.value)}
              className="rounded-cf border border-borda bg-superficie px-2 py-1 text-tinta"
            >
              <option value="todas">todas</option>
              {origens.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
          {selecionadas.length > 0 && !arquivadas && (
            <button
              type="button"
              onClick={() => {
                setRecado(null);
                setAcao({ tipo: "arquivar-lote", linhas: selecionadas });
              }}
              className="pill-cta"
            >
              Arquivar {selecionadas.length} selecionada(s)
            </button>
          )}
        </div>
      )}

      {/* ---- a fila --------------------------------------------------------- */}
      {linhas.length === 0 ? (
        <div className="surface p-5">
          <Vazio>
            {arquivadas
              ? "Nada arquivado. Toda pendência que existe está na fila aberta."
              : "Nada na fila — a esteira aplicou tudo."}{" "}
            Lido às {dataHora(estado.lidoEm)}.
          </Vazio>
        </div>
      ) : visiveis.length === 0 ? (
        <div className="surface p-5">
          <Vazio>Nenhuma pendência com esses filtros. Há {numero(linhas.length)} no total.</Vazio>
        </div>
      ) : (
        grupos.map((g) => (
          <GrupoDeClasse
            key={g.chave}
            grupo={g}
            colapsado={colapsados.has(g.chave)}
            aoAlternar={() => alternarGrupo(g.chave)}
            arquivadas={arquivadas}
            selecao={selecao}
            aoSelecionar={alternarSelecao}
            aoAgir={(a) => {
              setRecado(null);
              setAcao(a);
            }}
          />
        ))
      )}

      {/* ---- a outra fila, que ainda não tem tela --------------------------- */}
      {estadoPainel.fase === "pronto" && (
        <p className="border-t border-borda pt-4 text-xs leading-relaxed text-cinza">
          A sincronização de senha com o Memberkit tem fila própria, de outro
          subsistema, e ainda <strong>não tem tela</strong>:{" "}
          {numero(estadoPainel.painel.mk_falhas)} falha(s) hoje. Enquanto isso, ela
          se lê pelo runbook <code>sincronizacao-de-senha-memberkit.md</code>.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Um grupo de pendências que falharam pelo MESMO motivo
// ---------------------------------------------------------------------------

type Grupo = {
  chave: string;
  severidade: "falha" | "aviso";
  classe: string;
  itens: LinhaTriagem[];
};

function GrupoDeClasse({
  grupo,
  colapsado,
  aoAlternar,
  arquivadas,
  selecao,
  aoSelecionar,
  aoAgir,
}: {
  grupo: Grupo;
  colapsado: boolean;
  aoAlternar: () => void;
  arquivadas: boolean;
  selecao: Set<string>;
  aoSelecionar: (chave: string) => void;
  aoAgir: (a: Acao) => void;
}) {
  const semAlvo = grupo.itens.every((l) => !l.tem_fonte_para_transacao);
  const semAluno = grupo.itens.every((l) => l.aluno_procedencia === "nao_identificado");
  const maisVelho = Math.max(...grupo.itens.map((l) => l.dias));

  return (
    <section className="surface overflow-hidden p-0">
      <button
        type="button"
        onClick={aoAlternar}
        aria-expanded={!colapsado}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 p-4 text-left sm:p-5"
      >
        <Chip tom={grupo.severidade === "falha" ? "erro" : "atencao"}>
          {grupo.severidade === "falha" ? "falha" : "aviso"}
        </Chip>
        <span className="font-display text-sm font-bold text-titulo">
          {grupo.classe}
        </span>
        <span className="text-sm text-cinza tabular-nums">
          {numero(grupo.itens.length)} pendência(s) · mais antiga há {maisVelho} dia(s)
        </span>
        <span className="ml-auto text-cinza" aria-hidden>
          <IconeDivisa className={`size-[18px] transition-transform ${colapsado ? "" : "rotate-180"}`} />
        </span>
        <span className="sr-only">{colapsado ? "mostrar as pendências" : "esconder as pendências"}</span>
      </button>

      {/* A nota derivada do banco: é o que responde "isso tem conserto?" antes de
          o operador abrir dezoito linhas iguais. */}
      <div className="border-t border-borda px-4 pb-1 pt-3 text-xs leading-relaxed text-cinza sm:px-5">
        {grupo.severidade === "aviso" ? (
          <p>
            Aviso é informação sobre uma aplicação que <strong>aconteceu</strong>, não
            trabalho pendente. Reprocessar apagaria o registro de que foi aplicado, e o
            banco recusa. O desfecho aqui é arquivar com motivo.
          </p>
        ) : semAlvo ? (
          <p>
            Nenhuma delas tem fonte em <code>acessos</code> para a transação citada —
            reprocessar vai falhar igual. Se for estorno de venda anterior ao hub, o
            desfecho é arquivar com motivo, e a linha volta sozinha se a transação
            aparecer.
          </p>
        ) : (
          <p>
            Ao menos uma tem fonte em <code>acessos</code> para a transação, então
            reprocessar tem alvo.
          </p>
        )}
        {semAluno && grupo.severidade === "falha" && (
          <p className="mt-1">
            E nenhuma identifica o aluno: o payload de cancelamento só traz a transação.
            O conserto é no adaptador, não aqui — ver <code>docs/cancelamentos-orfaos.md</code>.
          </p>
        )}
      </div>

      {!colapsado && (
        <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-5 sm:pb-5">
          <table className="w-full min-w-[52rem] text-left">
            <thead>
              <tr>
                {!arquivadas && <Th> </Th>}
                <Th>quando</Th>
                <Th>origem</Th>
                <Th>aluno</Th>
                <Th>transação</Th>
                <Th>{arquivadas ? "arquivada por" : "o que fazer"}</Th>
              </tr>
            </thead>
            <tbody>
              {grupo.itens.map((l) => (
                <LinhaDaFila
                  key={chaveDe(l)}
                  linha={l}
                  arquivadas={arquivadas}
                  selecionada={selecao.has(chaveDe(l))}
                  aoSelecionar={() => aoSelecionar(chaveDe(l))}
                  aoAgir={aoAgir}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LinhaDaFila({
  linha,
  arquivadas,
  selecionada,
  aoSelecionar,
  aoAgir,
}: {
  linha: LinhaTriagem;
  arquivadas: boolean;
  selecionada: boolean;
  aoSelecionar: () => void;
  aoAgir: (a: Acao) => void;
}) {
  const l = linha;
  return (
    <tr className="border-b border-borda/60 align-top last:border-0">
      {!arquivadas && (
        <td className="py-2.5 pr-3">
          <input
            type="checkbox"
            checked={selecionada}
            onChange={aoSelecionar}
            aria-label={`selecionar pendência ${l.fonte} ${l.ref_id}`}
            className="mt-0.5 accent-laranja"
          />
        </td>
      )}
      <td className="py-2.5 pr-3 text-sm text-cinza tabular-nums">
        há {l.dias} dia(s)
        <span className="block text-xs">#{l.ref_id}</span>
      </td>
      <td className="py-2.5 pr-3 text-sm text-tinta">
        {l.origem}
        <span className="block text-xs text-cinza">{l.tipo ?? "webhook cru"}</span>
      </td>
      <td className="py-2.5 pr-3 text-sm">
        {l.aluno_procedencia === "nao_identificado" ? (
          <span className="text-cinza">
            não identificável
            <span className="block text-xs">o payload só traz a transação</span>
          </span>
        ) : (
          <>
            <span className="break-all text-tinta">{l.aluno_email}</span>
            <span className="block text-xs text-cinza">{procedencia(l.aluno_procedencia)}</span>
            {l.produtos && <span className="block text-xs text-cinza">{l.produtos}</span>}
          </>
        )}
      </td>
      <td className="py-2.5 pr-3">
        <code className="text-xs break-all text-cinza">{l.id_transacao ?? "—"}</code>
        {l.tem_fonte_para_transacao && (
          <span className="mt-1 block text-xs text-laranja-texto">tem fonte em acessos</span>
        )}
      </td>
      <td className="py-2.5">
        {arquivadas ? (
          <div className="text-xs leading-relaxed text-cinza">
            <span className="block text-tinta">{l.arquivada_por ?? "—"}</span>
            <span className="block">{l.arquivada_em ? dataHora(l.arquivada_em) : "—"}</span>
            {l.arquivada_motivo && <span className="mt-0.5 block italic">{l.arquivada_motivo}</span>}
            <button
              type="button"
              onClick={() => aoAgir({ tipo: "desarquivar", linha: l })}
              title="Devolver à fila"
              aria-label="Devolver à fila"
              className="grid size-9 shrink-0 place-items-center rounded-full border-[1.5px] border-borda text-tinta transition hover:border-laranja hover:text-laranja-texto mt-2"
            >
              <IconeDevolver />
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {l.pode_reprocessar && (
              <button
                type="button"
                onClick={() => aoAgir({ tipo: "reprocessar", linha: l })}
                title="Reprocessar a pendência"
                aria-label="Reprocessar a pendência"
                className="grid size-9 shrink-0 place-items-center rounded-full border-[1.5px] border-borda text-tinta transition hover:border-laranja hover:text-laranja-texto"
              >
                <IconeReprocessar />
              </button>
            )}
            <button
              type="button"
              onClick={() => aoAgir({ tipo: "arquivar", linha: l })}
              title="Arquivar esta pendência"
              aria-label="Arquivar esta pendência"
              className="grid size-9 shrink-0 place-items-center rounded-full border-[1.5px] border-borda text-tinta transition hover:border-laranja hover:text-laranja-texto"
            >
              <IconeArquivar />
            </button>
            {l.aluno_profile_id && (
              <a
                href="/admin/alunos"
                title="Abrir o dossiê do aluno"
                aria-label="Abrir o dossiê do aluno"
                className="grid size-9 shrink-0 place-items-center rounded-full border-[1.5px] border-borda text-tinta transition hover:border-laranja hover:text-laranja-texto"
              >
                <IconeDossie />
              </a>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// A única escrita, com motivo obrigatório
// ---------------------------------------------------------------------------

type Acao =
  | { tipo: "arquivar"; linha: LinhaTriagem }
  | { tipo: "desarquivar"; linha: LinhaTriagem }
  | { tipo: "reprocessar"; linha: LinhaTriagem }
  | { tipo: "arquivar-lote"; linhas: LinhaTriagem[] };

type Recado = { tom: "ok" | "atencao" | "erro"; texto: string };

const TITULOS: Record<Acao["tipo"], string> = {
  arquivar: "Arquivar esta pendência",
  desarquivar: "Devolver à fila",
  reprocessar: "Reprocessar a pendência",
  "arquivar-lote": "Arquivar as selecionadas",
};

function FormularioDeAcao({
  acao,
  aoFechar,
}: {
  acao: Acao;
  aoFechar: (r: Recado | null) => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);

    if (acao.tipo === "arquivar-lote") {
      // O banco é singular de propósito: a auditoria é POR pendência, não por
      // lote. Então são N chamadas em sequência — e elas NÃO são atômicas. Se a
      // oitava falhar, as sete anteriores ficaram arquivadas, e a tela diz isso
      // em vez de fingir tudo-ou-nada.
      let feitas = 0;
      const falharam: string[] = [];
      for (const l of acao.linhas) {
        const r = await arquivarPendencia(l.fonte, l.ref_id, motivo);
        if (r.ok) feitas++;
        else falharam.push(`#${l.ref_id}: ${r.mensagem}`);
      }
      setSalvando(false);
      aoFechar({
        tom: falharam.length === 0 ? "ok" : "atencao",
        texto:
          falharam.length === 0
            ? `${feitas} pendência(s) arquivada(s).`
            : `${feitas} arquivada(s), ${falharam.length} não: ${falharam.join(" · ")}`,
      });
      return;
    }

    if (acao.tipo === "arquivar") {
      const r = await arquivarPendencia(acao.linha.fonte, acao.linha.ref_id, motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      return aoFechar({
        tom: "ok",
        texto: `Arquivada. Restam ${numero(r.res.abertas_restantes)} pendência(s) aberta(s).`,
      });
    }

    if (acao.tipo === "desarquivar") {
      const r = await desarquivarPendencia(acao.linha.fonte, acao.linha.ref_id, motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      return aoFechar({
        tom: "ok",
        texto: `De volta à fila. Agora são ${numero(r.res.abertas_restantes)} aberta(s).`,
      });
    }

    const r = await reprocessarPendencia(acao.linha.ref_id, motivo);
    setSalvando(false);
    if (!r.ok) return setErro(r.mensagem);

    // O desfecho descreve o que o BANCO mediu. "Salvo" seria mentira em 18 de 18
    // dos casos de hoje.
    const res = r.res;
    if (res.aplicou) {
      return aoFechar({ tom: "ok", texto: "Aplicado — a pendência saiu da fila." });
    }
    if (res.erro_igual) {
      return aoFechar({
        tom: "atencao",
        texto: `Reprocessado e NÃO resolveu: o erro é o mesmo de antes — ${res.erro_novo}. A causa não foi corrigida; se não houver conserto, arquive com motivo.`,
      });
    }
    return aoFechar({
      tom: "atencao",
      texto: `Reprocessado e o erro MUDOU: ${res.erro_novo}. Continua na fila, agora como ${res.severidade_nova}.`,
    });
  }

  const alvo =
    acao.tipo === "arquivar-lote"
      ? `${acao.linhas.length} pendência(s)`
      : `#${acao.linha.ref_id} · ${acao.linha.origem} · ${acao.linha.tipo ?? "webhook"}`;

  return (
    <form onSubmit={enviar} className="surface p-4 sm:p-5">
      <h3 className="font-display text-base font-bold text-titulo">{TITULOS[acao.tipo]}</h3>
      <p className="mt-0.5 text-sm text-cinza">{alvo}</p>

      {acao.tipo === "reprocessar" && (
        <p className="mt-3 rounded-cf bg-alerta-fundo px-3 py-2 text-sm leading-relaxed text-alerta-tinta">
          Reprocessar reaplica o evento como ele foi recebido. Se a causa não foi
          corrigida, vai falhar igual — e a tentativa fica registrada com seu nome.
        </p>
      )}
      {acao.tipo === "arquivar" && acao.linha.severidade === "aviso" && (
        <p className="mt-3 text-sm leading-relaxed text-cinza">
          Arquivar não desfaz nada: o evento foi aplicado, e o registro disso continua
          intacto. Só sai da frente.
        </p>
      )}

      <div className="mt-4">
        <label htmlFor="motivo" className="text-sm font-semibold text-tinta">
          Motivo (obrigatório)
        </label>
        <textarea
          id="motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          required
          placeholder="ex.: estorno de venda anterior a 04/08 — não existe transação correspondente no hub"
          className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta placeholder:text-cinza/70"
        />
        <p className="mt-1 text-xs leading-relaxed text-cinza">
          Fica gravado com seu nome. Escreva para quem vai ler isso em seis meses sem
          lembrar do caso.
        </p>
      </div>

      {erro && (
        <p role="alert" className="mt-3 rounded-cf bg-erro-fundo px-3 py-2 text-sm text-erro-tinta">
          {erro}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando || motivo.trim() === ""} className="pill-cta">
          {salvando ? "Gravando…" : "Confirmar"}
        </button>
        <button type="button" onClick={() => aoFechar(null)} className="btn-contorno">
          Desistir
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

/** A chave da linha é o PAR: são duas sequências independentes. */
function chaveDe(l: LinhaTriagem): string {
  return `${l.fonte}:${l.ref_id}`;
}

/**
 * A identidade do formulário aberto — é o que faz o React DESMONTAR o formulário
 * quando o operador troca de pendência com ele aberto.
 *
 * Sem `key`, o elemento ocupa a mesma posição na mesma lista de filhos, a
 * instância é reaproveitada, e o `motivo` (e o `erro`) escritos para uma linha
 * chegam na próxima. Confirmar ali grava o texto de uma pendência como a
 * justificativa permanente de outra — e `triagem_arquivada.motivo` é exatamente
 * o campo que responde "por que essa linha saiu da frente" seis meses depois.
 *
 * O agravante que torna isso invisível: 18 das 27 pendências de hoje são da MESMA
 * classe, então o texto carregado ("estorno de venda anterior ao hub") LÊ como
 * correto para a linha vizinha. O operador não tem pista do erro.
 *
 * Preservar o texto não tem ganho nenhum, e é o que fecha a questão: depois de
 * confirmar com sucesso, `fechar` zera `acao` e o formulário desmonta de qualquer
 * jeito. O texto só sobrevivia quando o operador MUDAVA de ideia — o caso ruim.
 */
function chaveDaAcao(a: Acao): string {
  // O lote é chaveado pela LISTA, não pela contagem: trocar uma selecionada por
  // outra mantém o número e manteria o motivo da seleção anterior.
  return a.tipo === "arquivar-lote"
    ? `arquivar-lote:${a.linhas.map(chaveDe).join(",")}`
    : `${a.tipo}:${chaveDe(a.linha)}`;
}

function procedencia(p: LinhaTriagem["aluno_procedencia"]): string {
  if (p === "canonico") return "e-mail veio no payload da compra";
  if (p === "acessos") return "derivado das compras dessa transação";
  if (p === "bruto") return "veio do payload cru da Guru";
  return "";
}
